import React, { Fragment, useEffect, useRef, useState } from "react";
import {
  StandaloneSearchBox,
  GoogleMap,
  MarkerF,
  CircleF,
} from "@react-google-maps/api";
import {
  FormControl,
  MenuItem,
  Select,
  TextField,
  SelectChangeEvent,
  Button,
  Box,
  useTheme,
  Grid,
  Input,
  Typography,
} from "@mui/material";
import LocationPoint from "../types/LocationPoint";
import MapMarker from "../types/MapMarker";
import Location from "../types/Location";
import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import { useDebounce } from "../hooks/useDebounce";

// TODO: Switch to env variable process.env.REACT_APP_DEFAULT_RADIUS_IN_FEET
const DEFAULT_RADIUS_IN_FEET = 100;
const MAX_PROJECT_ADDRESSES = 5;

export const PlaceComponent = (props: any) => {
  const inputRefs: any = useRef([]);
  // props.fieldValue passes in as an empty string when it is a new project
  const existingLocations = Array.isArray(props.fieldValue)
    ? props.fieldValue
    : [{ address: "", radius: DEFAULT_RADIUS_IN_FEET }];
  const [locations, setLocations] = useState<Location[]>(existingLocations);
  const [addressError, setAddressError] = useState<string[]>([]);
  const containerStyle = {
    width: "100%",
    height: "400px",
    borderRadius: "10px",
  };
  const radiusOptions: Array<number> = [
    100, 200, 300, 400, 500, 750, 1000, 2000, 3000, 4000, 5000, 10000,
  ];

  const addLocationFields = () => {
    // Add a new set of address fields
    setLocations([
      ...locations,
      { address: "", radius: DEFAULT_RADIUS_IN_FEET } as Location,
    ]);
    setAddressError([...addressError, ""]);
    setLocationDataForForm();
  };
  

  const removeLocationFields = (index: number) => {
    // Remove the last set of address fields
    let newLocations = [...locations];
    newLocations.splice(index, 1);
    setLocations(newLocations);
    setAddressError(prevErrors => {
      const newErrors = [...prevErrors];
      newErrors.splice(index, 1);
      return newErrors;
    });
    setLocationDataForForm();
  };
  
  const handlePlaceChanged = (
    index: number,
    radiusInFeet: number = DEFAULT_RADIUS_IN_FEET
  ) => {
    // Handles the autocomplete address fields
    // and updating of the map marker and map circle
    if (!inputRefs.current[index]) {
      // Each project address needs its own reference
      inputRefs.current[index] = React.createRef();
    }
    const place = inputRefs.current[index].getPlaces();
  
    if (place) {
      const currentAddress = place[0].formatted_address;
      const duplicateIndex = locations.findIndex(
        (location, i) => i !== index && location.address === currentAddress
      );  
      if (duplicateIndex !== -1) {
        setAddressError((prevErrors) => {
          const newErrors = [...prevErrors];
          newErrors[index] = `This location already exists`;
          return newErrors;
        });
        return;
      } else {
        setAddressError((prevErrors) => {
          const newErrors = [...prevErrors];
          newErrors[index] = "";
          return newErrors;
        });
      }
        // console.log(place[0].formatted_address)
      // console.log(place[0].geometry.location.lat())
      // console.log(place[0].geometry.location.lng())

      // Store the address, lat, long, radius in state
      const center: LocationPoint = {
        lat: place[0].geometry.location.lat(),
        lng: place[0].geometry.location.lng(),
      };
      const marker: MapMarker = {
        position: center,
        label: {
          color: "white",
          text: "P",
        },
        draggable: true,
        circle: {
          radius: {
            feet: radiusInFeet || DEFAULT_RADIUS_IN_FEET,
            meters: radiusInFeet
              ? feetToMeters(radiusInFeet)
              : feetToMeters(DEFAULT_RADIUS_IN_FEET),
          },
          options: {
            strokeColor: "#ff0000",
          },
        },
      };
      const loc: Location = {
        address: place[0].formatted_address,
        latitude: place[0].geometry.location.lat(),
        longitude: place[0].geometry.location.lng(),
        radius: radiusInFeet,
        marker: marker,
      };

      if (!locations[index]) {
        setLocations((locations) => [...locations, loc]);
      } else {
        setLocations((locations) => {
          return locations.map((location, i) => (i === index ? loc : location));
        });
      }
      setLocationDataForForm();
    } else {
      // Just update the marker's radius
      setLocations((locations) => {
        return locations.map((location, i) => {
          if (i === index && location.radius && location.marker) {
            location.radius = radiusInFeet || DEFAULT_RADIUS_IN_FEET;
            location.marker.circle.radius = {
              feet: radiusInFeet || DEFAULT_RADIUS_IN_FEET,
              meters: radiusInFeet
                ? feetToMeters(radiusInFeet)
                : feetToMeters(DEFAULT_RADIUS_IN_FEET),
            };
          }
          return location;
        });
      });
      setLocationDataForForm();
    }
  };

  const onRadiusChange = (event: SelectChangeEvent, index: number) => {
    const radius = parseInt(event.target.value);
    handlePlaceChanged(index, radius);
  };

  const onAddressChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    event.preventDefault();
    const updatedLocation: Location = {
      address: event.target.value,
    };
    setLocations((locations) => {
      return locations.map((location, i) =>
        i === index ? updatedLocation : location
      );
    });
    const nativeEvent = event.nativeEvent as InputEvent;
    if (nativeEvent.inputType === "deleteContentBackward" && event.target.value === "") {
      // Clear the text field's data
      event.target.value = "";
      setAddressError([]);
    }
  };

  const setLocationDataForForm = useDebounce(() => {
    // Use debounce to prevent crazy number of calls
    // Transform location data for form
    if (locations && locations.length > 0) {
      const location = {
        name: "locations",
        value: locations,
      };
      // Store location data in form state
      props.onDataEntry(location);
    }
  });

  // Converts feet into meters
  const feetToMeters = (feet: number) => {
    return feet * 0.3048;
  };

  useEffect(() => {
    setLocations(locations);
  }, [existingLocations, locations]);

  // Converts meters into feet
  // const metersToFeet = (meters: number) => {
  //     return meters * 3.28084;
  // }
  const hasError = addressError.some(error => error !== "");
  const theme = useTheme();

  return (
    <>
      {locations
        ? locations.map((location, index) => (
            <div key={index}>
              <Grid container spacing={4} mb={4}>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={12}
                  lg={6}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexDirection: "column",
                    "& div, & p": {
                      width: "100%",
                    },
                  }}
                >
                  <Typography
                    fontSize={"14px"}
                    color="#5A525B"
                    paddingBottom={0.5}
                    fontWeight="bold"
                  >
                    Location
                  </Typography>
                  <StandaloneSearchBox
                    onLoad={(ref: any) => (inputRefs.current[index] = ref)}
                    onPlacesChanged={() => handlePlaceChanged(index)}
                  >
                    <Box
                      sx={{
                        backgroundColor: "white",
                        borderRadius: "8px",
                        border: "1px solid #DBDBDB",
                        width: "100%",
                        padding: theme.spacing(0.8, 1.6, 0.8, 1.6),
                      }}
                      display={"flex"}
                      alignItems={"center"}
                      justifyContent={"center"}
                    >
                      <Input
                        disableUnderline
                        sx={{
                          width: "100%",
                          fontSize: "17px",
                        }}
                        placeholder={"Enter Location"}
                        key={`${index}_searchbox`}
                        id={`${index}_${props.field.name}`}
                        name={`${index}_${props.field.name}`}
                        type="text"
                        fullWidth={true}
                        value={location.address}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          onAddressChange(e, index)
                        }
                        required={true}
                      />
                    </Box>
                  </StandaloneSearchBox>
                  {addressError[index] && (
                  <Typography color="error" variant="body2"sx={{marginTop:"10px"}}>
                  {addressError[index]}
                  </Typography>
                  )}
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={12}
                  lg={6}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexDirection: "column",
                    "& div, & p": {
                      width: "100%",
                    },
                  }}
                >
                  <Typography
                    fontSize={"14px"}
                    color="#5A525B"
                    paddingBottom={0.5}
                    fontWeight="bold"
                  >
                    Distance
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "8px",
                      border: "1px solid #DBDBDB",
                    }}
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"center"}
                  >
                    <Select
                      displayEmpty
                      id={`${index}_radius`}
                      name={`${index}_radius`}
                      value={location.radius ? location.radius.toString() : ""}
                      onChange={(e: SelectChangeEvent) =>
                        onRadiusChange(e, index)
                      }
                      renderValue={(selected) => {
                        if (!selected) {
                          return "Select a Location Radius";
                        }
                        return `${selected} feet`;
                      }}
                      sx={{
                        width: "100%",
                        border: "none",
                        boxShadow: "none",
                        padding: theme.spacing(0.8, 1.6, 0.8, 1.6),
                        "& .MuiSelect-select": {
                          padding: theme.spacing(0.8, 0, 0.8, 0),
                        },
                        "&:hover, & fieldset, & fieldset:hover": {
                          border: "none",
                          // padding: 0,
                          margin: 0,
                          boxShadow: "none",
                        },
                      }}
                      required={true}
                    >
                      <MenuItem disabled value="">
                        Select a Location Radius
                      </MenuItem>
                      {radiusOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option} feet
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Grid>
              </Grid>
              {location.marker && (
                <Box key={index} className="formfield">
                  <GoogleMap
                    key={`${index}_map`}
                    id={`${index}_map`}
                    mapContainerStyle={containerStyle}
                    center={location.marker.position}
                    zoom={15}
                    options={{
                      clickableIcons: false,
                      disableDefaultUI: true,
                    }}
                  >
                    <Fragment>
                      <MarkerF
                        position={location.marker.position}
                        label={location.marker.label}
                      >
                        {location.marker.circle && (
                          <CircleF
                            center={{
                              lat: location.marker.position.lat,
                              lng: location.marker.position.lng,
                            }}
                            radius={location.marker.circle.radius.meters}
                            options={location.marker.circle.options}
                          />
                        )}
                      </MarkerF>
                    </Fragment>
                  </GoogleMap>
                </Box>
              )}
              <div className="button-section">
                {index ? (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => removeLocationFields(index)}
                  >
                    <RemoveIcon />
                  </Button>
                ) : null}
                {index === locations.length - 1 &&
                locations.length < MAX_PROJECT_ADDRESSES ? (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => addLocationFields()}
                  >
                    <AddIcon />
                  </Button>
                ) : null}
              </div>
            </div>
          ))
        : null}
    </>
  );
};

export default PlaceComponent;
